import { useEffect, type ReactNode } from "react";
import { BrandMark } from "./BrandMark.js";
import { markBrandIntroStart } from "./brandIntro.js";

interface BrandScreenProps {
  /** Draws the mark in with the intro animation (loading splash) vs. static (signed-out). */
  animated?: boolean;
  /** Extra classes on the root — e.g. the splash's fixed-overlay + fade. */
  className?: string;
  /** Content below the mark: the loading cue, or the signed-out status + button. */
  children: ReactNode;
  role?: string;
  "aria-label"?: string;
}

/**
 * The full-viewport branded canvas shared by the loading splash and the
 * signed-out screen: theme-aware background, drifting dotted grid, radial
 * glow, and the Node-M mark, with a slot beneath it. One shell so the two
 * brand moments read as the same thing.
 */
export function BrandScreen({ animated = false, className = "", children, role, "aria-label": ariaLabel }: BrandScreenProps) {
  // Start the shared intro clock alongside the CSS animation, so the splash's
  // dismissal and any auth redirect can both wait the sequence out — see
  // brandIntro.ts.
  useEffect(() => {
    if (animated) markBrandIntroStart();
  }, [animated]);

  return (
    <div
      className={`mmm-brand-screen ${animated ? "mmm-brand-screen--animated" : ""} ${className}`.trim()}
      role={role}
      aria-label={ariaLabel}
    >
      <div className="mmm-brand-grid" />
      <div className="mmm-brand-stage">
        <div className="mmm-brand-glow" />
        <BrandMark />
      </div>
      {children}
    </div>
  );
}
